import { useEffect, useRef, useState } from "react";
import api from "@helpers/api.js";
import { fetchWallets, WALLET_PROVIDERS } from "@helpers/wallets";
import validate from "@helpers/validate.js";

export const useBankOnboarding = ({ onApproved } = {}) => {
    // Step 1 – personal info
    const [getName, setGetName] = useState('');
    const [getPhone, setGetPhone] = useState('');

    // Step 2 – address
    const [getAddress, setGetAddress] = useState(null);

    // Step 3 – accounting info
    const [getLegalType, setGetLegalType] = useState('physical');
    const [getDocument, setGetDocument] = useState('');
    const [getBirthDate, setGetBirthDate] = useState('');
    const [getCompanyType, setGetCompanyType] = useState('MEI');
    const [getIncome, setGetIncome] = useState('');
    const [getPixKeyType, setGetPixKeyType] = useState('CPF');
    const [getPixKey, setGetPixKey] = useState('');

    // API data
    const [account, setAccount] = useState(null);
    const [loadingWalletStatus, setLoadingWalletStatus] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState(null);
    const [creatingProgress, setCreatingProgress] = useState(0); // 18 → 0 countdown

    // Fires onApproved at most once, so the wallet-gate deep-link return
    // (/splitInvite → /banking?return=… → back) doesn't redirect repeatedly.
    const approvedFiredRef = useRef(false);

    // Accordion state
    const [currentStep, setCurrentStep] = useState(1);
    const [completedSteps, setCompletedSteps] = useState({
        1: false,
        2: false,
        3: false,
        4: false,
        5: false,
    });

    useEffect(() => {
        fetchWalletStatus();
    }, []);

    // 18-second countdown after wallet creation POST
    useEffect(() => {
        if (creatingProgress <= 0) return;
        const timer = setTimeout(() => {
            if (creatingProgress === 1) fetchWalletStatus();
            setCreatingProgress(prev => prev - 1);
        }, 1000);
        return () => clearTimeout(timer);
    }, [creatingProgress]);

    async function fetchWalletStatus() {
        try {
            const { asaas } = await fetchWallets();
            const accountData = asaas.raw || asaas;
            setAccount(accountData);

            // Wallet-gate (§4): once the account is approved, hand control back to
            // the caller (e.g. navigate to the /splitInvite deep link). One-shot.
            if (asaas?.statuses?.general === "approved" && !approvedFiredRef.current) {
                approvedFiredRef.current = true;
                onApproved?.();
            }

            if (accountData?.id) {
                const docsObj = accountData?.provider?.pending_documents || [];
                const docsFinished = docsObj.length === 0 || docsObj.every(doc => doc.status?.toLowerCase() === 'approved');

                const docStatus = accountData?.statuses?.documentation;
                setCompletedSteps(prev => ({
                    ...prev,
                    1: true, 2: true, 3: true,
                    4: docsFinished
                }));
                if (docsFinished || docStatus === 'awaiting_approval') {
                    setCurrentStep(5);
                } else {
                    setCurrentStep(4);
                }
            }
        } catch (e) {
            console.error('Error fetching wallet status', e);
        } finally {
            setLoadingWalletStatus(false);
        }
    }

    // ── Step validators ────────────────────────────────────────────────────

    const isValidStep1 = () => {
        if (!getName || getName.trim().split(/\s+/).length < 2) return false;
        const digits = getPhone?.replace(/\D/g, '') || '';
        return digits.length >= 10;
    };

    const validateStep1 = () => {
        if (!getName || getName.trim().split(/\s+/).length < 2)
            return "Informe seu nome completo.";
        const digits = getPhone?.replace(/\D/g, '') || '';
        if (digits.length < 10) return "Informe um celular válido.";
        handleStepComplete(1);
        return null;
    };

    const isValidStep2 = () => {
        if (!getAddress || typeof getAddress !== 'object') return false;
        const required = ['zipcode', 'state', 'city', 'neighborhood', 'street', 'number'];
        return required.every(f => getAddress[f]?.trim());
    };

    const validateStep2 = () => {
        if (!getAddress || typeof getAddress !== 'object') return "Informe seu endereço corretamente.";
        if (!getAddress.zipcode?.trim()) return "Informe o CEP.";
        if (!getAddress.state?.trim()) return "Informe o estado.";
        if (!getAddress.city?.trim()) return "Informe a cidade.";
        if (!getAddress.neighborhood?.trim()) return "Informe o bairro.";
        if (!getAddress.street?.trim()) return "Informe a rua.";
        if (!getAddress.number?.trim()) return "Informe o número do endereço.";

        handleStepComplete(2);
        return null;
    };

    const isValidStep3 = () => {
        if (getLegalType === 'physical') {
            if (!getDocument || getDocument.replace(/\D/g, '').length !== 11) return false;
            if (!getBirthDate || getBirthDate.trim().length < 8) return false;
        } else {
            if (!getCompanyType) return false;
            if (!getDocument || getDocument.replace(/\D/g, '').length !== 14) return false;
        }
        if (!getIncome) return false;
        if (!getPixKey || getPixKey.trim() === '') return false;
        return true;
    };

    const validateStep3 = () => {
        if (getLegalType === 'physical') {
            if (!getDocument || getDocument.replace(/\D/g, '').length !== 11)
                return "Informe um CPF válido.";
            if (!getBirthDate || getBirthDate.trim().length < 8)
                return "Informe uma data de nascimento válida.";
        } else {
            if (!getCompanyType) return "Selecione o enquadramento da empresa.";
            if (!getDocument || getDocument.replace(/\D/g, '').length !== 14)
                return "Informe um CNPJ válido.";
        }
        if (!getIncome) return "Informe a renda/faturamento mensal.";
        if (!getPixKey || getPixKey.trim() === '') return "Informe sua chave Pix de recebimento.";
        
        if (!isValidStep1() || !isValidStep2()) {
            return "Por favor, preencha corretamente e conclua todos os passos anteriores.";
        }

        handleSubmitWallet(); // fire-and-forget; countdown will open step 4 content
        handleStepComplete(3);
        return null;
    };

    const validateStep4 = () => {
        handleStepComplete(4);
        return null;
    };

    const handleStepComplete = (step) => {
        setCompletedSteps(prev => ({ ...prev, [step]: true }));
        if (step < 5) setCurrentStep(step + 1);
        else setCurrentStep(null);
    };

    // ── Wallet creation ────────────────────────────────────────────────────

    async function handleSubmitWallet() {
        if (submitting) return;
        setSubmitting(true);
        setErrors(null);
        try {
            const addressData = getAddress || {};
            const payload = {
                provider: WALLET_PROVIDERS.ASAAS,
                withdrawal: {
                    type: "pix",
                    key: getPixKeyType === 'PHONE' 
                        ? '+55' + getPixKey.replace(/\D/g, '').replace(/^55/, '') 
                        : getPixKeyType === 'CPF' || getPixKeyType === 'CNPJ' 
                            ? getPixKey.replace(/\D/g, '')
                            : getPixKey.trim(),
                    keyType: getPixKeyType,
                },
                address: {
                    street: addressData.street || "",
                    number: addressData.number || "",
                    complement: addressData.complement || "",
                    neighborhood: addressData.neighborhood || "",
                    city: addressData.city || "",
                    state: addressData.state || "",
                    zipcode: addressData.zipcode || "",
                },
                owner: {
                    company_type: getLegalType === 'juridical' ? getCompanyType : 'MEI',
                    name: getName,
                    birthdate: getBirthDate,
                    monthly_income: getIncome.toString(),
                    document: {
                        number: getDocument.replace(/\D/g, ''),
                    },
                    type: getLegalType === 'physical' ? 'individual' : 'company',
                    phone: { number: getPhone.replace(/\D/g, '').replace(/^55/, '') },
                },
            };
            await api.post('wallet', payload);
            setCreatingProgress(18);
        } catch (e) {
            const message = e?.response?.data?.message || "Ocorreu um erro ao criar a conta.";
            setErrors(message);
        } finally {
            setSubmitting(false);
        }
    }

    // ── Document upload ────────────────────────────────────────────────────

    async function handleDocumentUpload(docItem, setDocLoading) {
        function fileToBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(file);
            });
        }

        const input = window.document.createElement('input');
        input.type = 'file';
        input.accept = 'image/png,image/jpeg,image/jpg,application/pdf';

        input.onchange = async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;

            if (file.size > 20 * 1024 * 1024) {
                window.setSnackbar?.('O arquivo deve ter no máximo 20MB.');
                return;
            }

            try {
                setDocLoading?.(true);
                const dataUrl = await fileToBase64(file);
                await api.post('wallet/document', {
                    id: docItem.id,
                    type: docItem.type,
                    document: dataUrl,
                });
                window.setSnackbar?.('Documento enviado com sucesso.');
                await fetchWalletStatus();
            } catch (e) {
                const message = e?.response?.data?.message || e.message || 'Falha ao enviar documento.';
                window.setSnackbar?.(message);
            } finally {
                setDocLoading?.(false);
                input.remove();
            }
        };

        input.click();
    }

    const walletCreated = !!account?.id;
    const walletInProgress = submitting || creatingProgress > 0;

    const isFormValid = isValidStep1() && isValidStep2() && isValidStep3();

    return {
        // Form
        getName, setGetName,
        getPhone, setGetPhone,
        getAddress, setGetAddress,
        getLegalType, setGetLegalType,
        getDocument, setGetDocument,
        getBirthDate, setGetBirthDate,
        getCompanyType, setGetCompanyType,
        getIncome, setGetIncome,
        getPixKeyType, setGetPixKeyType,
        getPixKey, setGetPixKey,
        // API / status
        account,
        loadingWalletStatus,
        submitting,
        errors,
        creatingProgress,
        walletCreated,
        walletInProgress,
        isFormValid,
        // Accordion control
        currentStep, setCurrentStep,
        completedSteps,
        // Validators
        validateStep1,
        validateStep2,
        validateStep3,
        validateStep4,
        // Actions
        handleDocumentUpload,
        handleRefreshStatus: fetchWalletStatus,
    };
};
